import { useCallback, useEffect, useState } from 'react';

import {
  isMarkOrBetter,
  RfqMarkPrice,
} from '@paradigm/features/src/unified-rfqs/ui/rfq-websocket/mark-price-context';
import {
  AggressSellProvider,
  AggressBuyProvider,
  AggressAction,
  AskProvider,
  BidProvider,
  useAggressBuyAction,
  useAggressSellAction,
} from '@paradigm/features/src/unified-rfqs/ui/one-click-trade/aggress-order-action-context';
import { useDrfqRfq } from '@paradigm/features/src/unified-rfqs/ui/rfq-context';
import { RfqOrder } from '@paradigm/features/src/unified-rfqs/entities/drfq/orders';
import {
  getEffectiveQuantityPrecision,
  getProduct,
} from '@paradigm/features/src/products/domain';
import { isDatedRfqLegs } from '@paradigm/features/src/unified-rfqs/ui/rfqs/legs-functions';

import { RowIndexProvider } from '#/components/UnifiedRfqs/components/RfqRowBase';
import {
  AggressBuyField,
  AggressSellField,
} from '#/components/UnifiedRfqs/one-click-trade/OneClickAction';
import {
  BuyContainer,
  SellContainer,
} from '#/components/UnifiedRfqs/drfq/Orderbook';
import { DeskName, MainRow } from '#/components/UnifiedRfqs/drfq/common';
import useIsMobile from '@paradigm/design-system/src/hooks/useIsMobile';
import RfqOrders from '#/components/UnifiedRfqs/drfq/RfqOrders';
import { AnonIconText } from '#/components/UnifiedRfqs/components/WarningMessage';
import { useBboView } from '@paradigm/features/src/unified-rfqs/ui/bbo-view-context';
import { useSubmitOrderTracker } from '../one-click-trade/useSubmitOrderTracker';

export interface Props {
  bid: RfqOrder | undefined;
  ask: RfqOrder | undefined;
  onPriceClick?: () => void;
  isExpanded: boolean;
  isViewDetails?: boolean;
  isMarketRow?: boolean;
}

export default function PrimaryOrdersRow({
  bid,
  ask,
  isExpanded,
  isViewDetails,
  onPriceClick,
  isMarketRow,
}: Props) {
  const bboView = useBboView();
  const mark = bboView.markPrice.formatted;
  const { getBboOffset, markPriceIv } = bboView;
  const [isSelling, setSelling] = useState(false);
  const [isBuying, setBuying] = useState(false);
  const isMobile = useIsMobile();

  const { offsetAsk, offsetBid, isBetterThanScreenAsk, isBetterThanScreenBid } =
    getBboOffset({
      bidPrice: bid?.price,
      askPrice: ask?.price,
    });

  const bidDesk = bid?.desk === null ? <AnonIconText /> : bid?.desk;
  const askDesk = ask?.desk === null ? <AnonIconText /> : ask?.desk;

  const sellingDesk = !isSelling && (
    <DeskName data-testid="desk-sell">{bidDesk}</DeskName>
  );
  const buyingDesk = !isBuying && (
    <DeskName data-testid="desk-buy">{askDesk}</DeskName>
  );

  return (
    <RowIndexProvider value={0}>
      <BidProvider value={bid}>
        <AskProvider value={ask}>
          <MainRow isExpanded={isExpanded} isViewDetails={isViewDetails}>
            <SellContainer css="grid-area: qsell;" isConfirmStep={isSelling}>
              {sellingDesk}
              {isMobile && <span />}
              <AggressSellProvider>
                <AggressSell
                  onStatusChange={(status) =>
                    setSelling(isConfirmingOrSubmitting(status))
                  }
                />
              </AggressSellProvider>
            </SellContainer>
            <RfqOrders
              isMarketRow={isMarketRow}
              isTopQuote
              bid={bid}
              ask={ask}
              offsetAsk={offsetAsk}
              offsetBid={offsetBid}
              markPriceIv={markPriceIv.formatted}
              onPriceClick={onPriceClick}
              mark={mark}
              isBetterThanScreenBid={isBetterThanScreenBid}
              isBetterThanScreenAsk={isBetterThanScreenAsk}
            />
            <BuyContainer css="grid-area: qbuy;" isConfirmStep={isBuying}>
              {isMobile && <span />}
              <AggressBuyProvider>
                <AggressBuy
                  onStatusChange={(status) =>
                    setBuying(isConfirmingOrSubmitting(status))
                  }
                />
              </AggressBuyProvider>
              {isMobile && <span />}
              {buyingDesk}
            </BuyContainer>
          </MainRow>
        </AskProvider>
      </BidProvider>
    </RowIndexProvider>
  );
}

interface AggressActionProps {
  readonly onStatusChange: (status: AggressAction['status']) => void;
}

function AggressBuy({ onStatusChange }: AggressActionProps) {
  const {
    status,
    isButtonEnabled,
    submitAggressOrder,
    lockedOrder,
    resetConfirmState,
  } = useAggressBuyAction();
  const rfq = useDrfqRfq();
  const markPrice = useBboView().markPrice.formatted;
  const isMarkOrBetterFn = makeIsMarkOrBetter(markPrice);
  const track = useSubmitOrderTracker({
    auctionType: 'RFQ',
    side: 'BUY',
    price: lockedOrder?.price,
    isMarkOrBetterFn,
    status,
  });

  useEffect(() => {
    onStatusChange(status);
  }, [onStatusChange, status]);

  const onSubmit: typeof submitAggressOrder = useCallback(
    (...args) => {
      track();
      submitAggressOrder(...args);
    },
    [submitAggressOrder, track],
  );

  return (
    <AggressBuyField
      rfqId={rfq.id}
      price={lockedOrder?.price}
      isFieldEnabled={false}
      resetConfirmState={resetConfirmState}
      onSubmit={onSubmit}
      quantity={rfq.quantity}
      status={status}
      isButtonEnabled={isButtonEnabled}
      quantityDecimals={getEffectiveQuantityPrecision(
        getProduct(rfq.product_codes[0]!),
        isDatedRfqLegs(rfq.legs, rfq.product_codes[0]),
      )}
      isMarkOrBetter={isMarkOrBetterFn}
      productCode={rfq.product_codes[0]}
      isFieldVisible={false}
    />
  );
}

function AggressSell({ onStatusChange }: AggressActionProps) {
  const {
    status,
    isButtonEnabled,
    submitAggressOrder,
    lockedOrder,
    resetConfirmState,
  } = useAggressSellAction();
  const rfq = useDrfqRfq();
  const markPrice = useBboView().markPrice.formatted;
  const isMarkOrBetterFn = makeIsMarkOrBetter(markPrice);
  const track = useSubmitOrderTracker({
    auctionType: 'RFQ',
    side: 'BUY',
    price: lockedOrder?.price,
    isMarkOrBetterFn,
    status,
  });

  useEffect(() => {
    onStatusChange(status);
  }, [onStatusChange, status]);

  const onSubmit: typeof submitAggressOrder = useCallback(
    (...args) => {
      track();
      submitAggressOrder(...args);
    },
    [submitAggressOrder, track],
  );

  return (
    <AggressSellField
      rfqId={rfq.id}
      price={lockedOrder?.price}
      isFieldEnabled={false}
      resetConfirmState={resetConfirmState}
      onSubmit={onSubmit}
      quantity={rfq.quantity}
      status={status}
      isButtonEnabled={isButtonEnabled}
      quantityDecimals={getEffectiveQuantityPrecision(
        getProduct(rfq.product_codes[0]!),
        isDatedRfqLegs(rfq.legs, rfq.product_codes[0]),
      )}
      isMarkOrBetter={isMarkOrBetterFn}
      productCode={rfq.product_codes[0]}
      isFieldVisible={false}
    />
  );
}

function isConfirmingOrSubmitting(status: AggressAction['status']) {
  return ['confirming', 'submitting'].includes(status);
}

function makeIsMarkOrBetter(markPrice: string): RfqMarkPrice['isMarkOrBetter'] {
  return (price, side) => isMarkOrBetter(markPrice, price, side);
}
